import { tUnchecked } from '@glow/language'
import classNames from 'classnames'
import i18next from 'i18next'
import { debounce } from 'lodash-es'
import React, { ReactNode, useState } from 'react'
import { Card, IconDragAndDrop, Text } from '../../atoms'

export interface CategoryColumn {
  label: string
  title?: string
  key: string
  category?: string
  fixedOrder?: number
}

export const MoveableCard = ({
  column,
  index,
  isDragging,
  dragIndex,
  onDrag,
  moveCard,
  countOffset = 0
}: {
  isDragging: boolean
  column: CategoryColumn
  dragIndex?: number
  index: number
  onDrag: (index?: number) => void
  moveCard: (dragIndex: number, hoverIndex: number) => void
  countOffset?: number
}) => {
  return (
    <DraggableEl dragIndex={dragIndex} index={index} onDrag={onDrag} moveCard={moveCard}>
      <Card
        className={classNames(
          'mt-2 mx-2 p-4 last:mb-2 grid cursor-move grid-cols-[auto_20px] items-center',
          isDragging ? 'opacity-30' : ''
        )}
      >
        <div>
          <Text as="span" textType="sm" semiBold className="text-emphasis-high mr-2">
            <span className="mr-1">{index + countOffset + 1}.</span>
            {column.label}
          </Text>
          <Text as="span" textType="sm">
            {i18next.exists(`columnCategories.${column.category}`)
              ? tUnchecked(`columnCategories.${column.category}`)
              : column.category}
          </Text>
        </div>
        <IconDragAndDrop />
      </Card>
    </DraggableEl>
  )
}

export const DraggableEl = ({
  index,
  dragIndex,
  as = 'div',
  children,
  onDrag,
  moveCard,
  className,
  disabled,
  onDragFinished,
  onDoubleClick
}: {
  index: number
  dragIndex?: number
  as?: string
  children: ReactNode
  className?: string
  onDrag?: (index?: number) => void
  moveCard: (dragIndex: number, hoverIndex: number) => void
  disabled?: boolean
  onDragFinished?: () => void
  onDoubleClick?: (event: React.MouseEvent<Element>) => void
}) => {
  const [isDraggingUp, setIsDraggingUp] = useState(false)
  const [isDraggingDown, setIsDraggingDown] = useState(false)
  const debounceIsDraggingUp = debounce(() => setIsDraggingUp(false), 50)
  const debounceIsDraggingDown = debounce(() => setIsDraggingDown(false), 50)
  const onDragStart = (event: React.DragEvent<Element>) => {
    event.dataTransfer.setData('text/plain', `${index}`)
    onDrag?.(index)
  }
  const onDragEnd = () => {
    onDrag?.(undefined)
    onDragFinished?.()
  }

  const onDragLeave = (event: React.DragEvent<Element>) => {
    event.preventDefault()
    debounceIsDraggingUp()
    debounceIsDraggingDown()
  }

  const onDragOver = (event: React.DragEvent<Element>) => {
    event.preventDefault()
    debounceIsDraggingUp.cancel()
    debounceIsDraggingDown.cancel()
    const draggingDirectionUp = dragIndex !== undefined && dragIndex > index

    if (!isDraggingUp && draggingDirectionUp) {
      setIsDraggingUp(true)
    } else if (!isDraggingDown && !draggingDirectionUp) {
      setIsDraggingDown(true)
    }
  }

  return React.createElement(
    as,
    {
      className: classNames(
        className,
        as === 'tr'
          ? isDraggingUp
            ? '[&>td]:shadow-border-top-primary-500'
            : isDraggingDown
              ? '[&>td]:shadow-border-bottom-primary-500'
              : ''
          : isDraggingUp
            ? 'border-t-2! border-b-0! border-primary-500!'
            : isDraggingDown
              ? 'border-b-2! border-t-0! border-primary-500!'
              : ''
      ),
      draggable: !disabled,
      onDoubleClick,
      onDragStart,
      onDragEnd,
      onDragLeave,
      onDragOver,
      onDrop: (event) => {
        event.preventDefault()
        debounceIsDraggingUp()
        debounceIsDraggingDown()

        const startIndex = event.dataTransfer.getData('text')
        moveCard(Number(startIndex), index)
        onDragEnd()
      }
    },
    children
  )
}
